import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { IconButton, Stack, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { AutoOrderConfigurationDto } from "../../../../features/portfolio-info/types/AutoOrderConfigurationDto";
import { AutoOrderConfigurationModel } from "../../../../features/portfolio-info/types/AutoOrderConfigurationModel";
import { AutoOrderState } from "../../../../features/portfolio-info/types/AutoOrderState";
import { DraggableDialogPaper } from "../DraggableDialogPaper";
import { DraggableDialogTitle } from "../DraggableDialogTitle";
import {
    AutoOrderParameters,
    AutoOrderParametersState,
    AUTO_ORDER_STRATEGY_VALUE
} from "./controls/AutoOrderParameters";

const createInitialParameters = (
    optionImpliedVolatility: number | null,
    underlyingPrice: number | null,
): AutoOrderParametersState => ({
    strategy: AUTO_ORDER_STRATEGY_VALUE,
    state: AutoOrderState.NotTriggered,
    stateDescription: null,
    isActive: false,
    targetPrice: underlyingPrice ?? 0,
    maxIvLevel: (optionImpliedVolatility ?? 0) + 0.05,
    maxPriceDelta: 100,
    netDelta: 0,
});

const mapConfigurationToParameters = (
    autoOrderConfiguration: AutoOrderConfigurationModel,
): AutoOrderParametersState => ({
    strategy: autoOrderConfiguration.strategy,
    state: autoOrderConfiguration.state,
    stateDescription: autoOrderConfiguration.stateDescription,
    isActive: autoOrderConfiguration.isActive,
    targetPrice: autoOrderConfiguration.targetPrice,
    maxIvLevel: autoOrderConfiguration.maxImpliedVolatilityLevel,
    maxPriceDelta: autoOrderConfiguration.maxPriceDelta,
    netDelta: autoOrderConfiguration.netDelta,
});

export function AutoOrderManagerDialog({
    isOpen,
    setIsOpen,
    portfolioItemId,
    optionOrderSymbol,
    optionOrderSize,
    optionImpliedVolatility,
    underlyingPrice,
    autoOrderConfiguration,
    onConfigurationSaved,
    saveAutoOrderConfiguration,
    fetchAutoOrderConfiguration,
    resetAutoOrderConfigurationState,
    icon,
}: {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    portfolioItemId: number;
    optionOrderSymbol: string;
    optionOrderSize: number;
    optionImpliedVolatility: number | null;
    underlyingPrice: number | null;
    autoOrderConfiguration: AutoOrderConfigurationModel | null;
    onConfigurationSaved: (configuration: AutoOrderConfigurationModel) => void;
    saveAutoOrderConfiguration: (
        portfolioItemId: number,
        autoOrderConfiguration: AutoOrderConfigurationDto,
    ) => Promise<AutoOrderConfigurationModel | null>;
    fetchAutoOrderConfiguration: (portfolioItemId: number) => Promise<AutoOrderConfigurationModel | null>;
    resetAutoOrderConfigurationState: (portfolioItemId: number) => Promise<AutoOrderConfigurationModel | null>;
    icon?: React.ReactNode;
}) {
    const [parameters, setParameters] = useState<AutoOrderParametersState>(
        autoOrderConfiguration !== null
            ? mapConfigurationToParameters(autoOrderConfiguration)
            : createInitialParameters(optionImpliedVolatility, underlyingPrice));
    const [isSaveProcessing, setIsSaveProcessing] = useState<boolean>(false);
    const [isRefreshProcessing, setIsRefreshProcessing] = useState<boolean>(false);
    const [isResetProcessing, setIsResetProcessing] = useState<boolean>(false);
    const shouldSkipNextConfigurationSync = useRef<boolean>(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (shouldSkipNextConfigurationSync.current) {
            shouldSkipNextConfigurationSync.current = false;
            return;
        }

        setParameters(autoOrderConfiguration !== null
            ? mapConfigurationToParameters(autoOrderConfiguration)
            : createInitialParameters(optionImpliedVolatility, underlyingPrice));
    }, [isOpen, optionOrderSymbol, optionOrderSize, optionImpliedVolatility, underlyingPrice, autoOrderConfiguration]);

    const handleParametersChange = (patch: Partial<AutoOrderParametersState>): void => {
        setParameters(prev => ({ ...prev, ...patch }));
    };

    const handleClose = (): void => {
        setIsOpen(false);
    };

    const handleSubmit = async (): Promise<boolean> => {
        if (portfolioItemId <= 0) {
            return false;
        }

        setIsSaveProcessing(true);

        try {
            const configuration = await saveAutoOrderConfiguration(portfolioItemId, buildDto());

            if (configuration !== null) {
                setParameters(mapConfigurationToParameters(configuration));
                onConfigurationSaved(configuration);
                return true;
            }

            return false;
        }
        finally {
            setIsSaveProcessing(false);
        }
    };

    const handleRefresh = async (): Promise<void> => {
        if (portfolioItemId <= 0) {
            return;
        }

        setIsRefreshProcessing(true);

        try {
            const configuration = await fetchAutoOrderConfiguration(portfolioItemId);

            if (configuration !== null) {
                setParameters(mapConfigurationToParameters(configuration));
                onConfigurationSaved(configuration);
            } else {
                setParameters(createInitialParameters(optionImpliedVolatility, underlyingPrice));
            }
        }
        finally {
            setIsRefreshProcessing(false);
        }
    };

    const handleResetOrderState = async (): Promise<void> => {
        if (portfolioItemId <= 0 || autoOrderConfiguration === null) {
            return;
        }

        setIsResetProcessing(true);

        try {
            const configuration = await resetAutoOrderConfigurationState(portfolioItemId);

            if (configuration !== null) {
                setParameters(prev => ({
                    ...prev,
                    state: configuration.state,
                    stateDescription: configuration.stateDescription,
                }));
                shouldSkipNextConfigurationSync.current = true;
                onConfigurationSaved(configuration);
            }
        }
        finally {
            setIsResetProcessing(false);
        }
    };

    const handleSubmitAndCloseDialog = async (): Promise<void> => {
        if (await handleSubmit()) {
            handleClose();
        }
    };

    const buildDto = (): AutoOrderConfigurationDto => ({
        Id: autoOrderConfiguration?.id ?? 0,
        PortfolioItemId: portfolioItemId,
        Strategy: parameters.strategy,
        IsActive: parameters.isActive,
        TargetPrice: parameters.targetPrice,
        MaxImpliedVolatilityLevel: parameters.maxIvLevel,
        MaxPriceDelta: Math.round(parameters.maxPriceDelta),
        NetDelta: parameters.netDelta,
    });

    const isConfirmDisabled = [
        parameters.targetPrice,
        parameters.maxIvLevel,
        parameters.maxPriceDelta,
    ].some(value => value <= 0) || isSaveProcessing || isRefreshProcessing || isResetProcessing;

    const isRefreshDisabled = portfolioItemId <= 0 || isRefreshProcessing || isSaveProcessing || isResetProcessing;

    const isResetOrderStateDisabled =
        portfolioItemId <= 0 ||
        autoOrderConfiguration === null ||
        isSaveProcessing ||
        isRefreshProcessing ||
        isResetProcessing ||
        (parameters.state === AutoOrderState.NotTriggered && (parameters.stateDescription ?? "") === "");

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            PaperComponent={DraggableDialogPaper}
            PaperProps={{
                component: "form",
            }}
            maxWidth={false}
        >
            <DraggableDialogTitle
                icon={icon}
                title={`Auto order${optionOrderSymbol ? `: ${optionOrderSymbol}` : ""}`}
                onClose={handleClose}
            />
            <DialogContent sx={{ paddingY: "11px", paddingX: "18px" }}>
                <AutoOrderParameters
                    optionOrderSymbol={optionOrderSymbol}
                    optionOrderSize={optionOrderSize}
                    parameters={parameters}
                    handleParametersChange={handleParametersChange}
                    onResetOrderState={() => void handleResetOrderState()}
                    isResetOrderStateDisabled={isResetOrderStateDisabled}
                />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between" }}>
                <Tooltip
                    title="Refresh auto order configuration"
                    slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                >
                    <span>
                        <IconButton
                            type="button"
                            color="primary"
                            onClick={handleRefresh}
                            disabled={isRefreshDisabled}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Stack direction="row" spacing={1}>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={isConfirmDisabled}
                        onClick={() => handleSubmit()}
                    >
                        Update
                    </Button>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={isConfirmDisabled}
                        onClick={() => handleSubmitAndCloseDialog()}
                    >
                        Update and close
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
